import { _ } from "../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ButtonGroupWidget from "../ButtonGroupWidget/ButtonGroupWidget.js";
import regionWidget from "../regionWidget.js";
import stacker from "../../elements/stacker/stacker.js";

/**
 * Render information about Capego integration for a particular company.
 *
 * A toggle allows the user to switch the company's import mode
 * between "auto" and "manual". A text explains the currently-selected
 * import mode.
 *
 * @param {CompanyCapegoIntegrationSettings} settings - Indicate the currently is currently being updated
 * @param {function} onSettingsChange - Called with no argument when `settings` has changed after a user action
 */
export default class CompanyCapegoIntegrationWidget extends Widget {
	constructor({ settings, onSettingsChange = () => {} } = {}) {
		super(...arguments);

		this._settings = settings;
		this._onSettingsChange = onSettingsChange;

		this._importModeButtons = this._makeButtonGroupWidget();

		this._importModeText = regionWidget({
			content: (html) => this._renderImportModeTextOn(html),
		});
	}

	renderContentOn(html) {
		html.div(
			{ class: "CapegoIntegrationWidget" },
			stacker(
				{ direction: "vertical", align: "left" },
				this._importModeButtons,
				this._importModeText,
			),
		);
	}

	_renderImportModeTextOn(html) {
		const manualImportText = _(
			'When an accounting period has been marked as "Quality assured" in Capego Closing of books it will not be automatically imported to finsit. The accounting data for the latest updated period of the fiscal year is available through regular import button.',
		);

		const autoImportText = _(
			'The accounting data will be automatically updated when an accounting period has been marked as "Quality assured" in Capego Closing of Books.',
		);

		html.render(
			this._settings.isManualImportMode()
				? manualImportText
				: autoImportText,
		);
	}

	_importModeLabelRenderer({ dataTestName, text }) {
		return (html) => html.div({ "data-test": dataTestName }, text);
	}

	_makeButtonGroupWidget() {
		const autoImportValue = 0;
		const manualImportValue = 1;

		const buttonGroup = new ButtonGroupWidget({
			states: [
				{
					label: this._importModeLabelRenderer({
						dataTestName: "set-to-auto",
						text: _("Auto"),
					}),
					value: autoImportValue,
					active: !this._settings.isManualImportMode(),
				},
				{
					label: this._importModeLabelRenderer({
						dataTestName: "set-to-manual",
						text: _("Manual"),
					}),
					value: manualImportValue,
					active: this._settings.isManualImportMode(),
				},
			],
		});

		buttonGroup.stateChanged.register(({ value }) => {
			if (value === autoImportValue)
				this._settings.changeToAutoImportMode();
			else this._settings.changeToManualImportMode();

			this._onSettingsChange();

			this._importModeText.update();
		});

		return buttonGroup;
	}
}
